// Shell banners: impersonation, store status, plan limits, plus a registry any page/module can
// use for app-wide notices: useShellBanner('payments-test-mode', { tone: 'warning', title: … }).
import { useEffect, useState, useSyncExternalStore } from 'react'
import { Link } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Eye, LogOut } from 'lucide-react'
import { Banner, type BannerProps } from '@/components/ui/feedback'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { useSession } from '@/lib/session'
import { api, ApiError } from '@/lib/api'

// ─── registry ───────────────────────────────────────────────────────────────────────────

export interface ShellBannerDef extends Omit<BannerProps, 'strip' | 'className'> { priority?: number }
let registry: Array<{ id: string; def: ShellBannerDef }> = []
const subs = new Set<() => void>()
const emit = () => subs.forEach(f => f())

/** Show an app-wide banner while mounted (and `def` is non-null). Higher priority first. */
export function useShellBanner(id: string, def: ShellBannerDef | null) {
  useEffect(() => {
    if (!def) return
    registry = [...registry.filter(r => r.id !== id), { id, def }].sort((a, b) => (b.def.priority ?? 0) - (a.def.priority ?? 0))
    emit()
    return () => { registry = registry.filter(r => r.id !== id); emit() }
    // Re-register when the visible content changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, def?.title, def?.tone, def?.priority, typeof def?.children === 'string' ? def.children : !!def])
}
const useRegistry = () => useSyncExternalStore(cb => { subs.add(cb); return () => { subs.delete(cb) } }, () => registry, () => registry)

/** Fire a plan-limit banner from anywhere (non react-query callers). */
export function announcePlanLimit(message: string) { window.dispatchEvent(new CustomEvent('plan:limit', { detail: message })) }

// ─── built-in banners ───────────────────────────────────────────────────────────────────

export function ShellBanners({ kind }: { kind: 'store' | 'platform' | 'full' | 'bare' }) {
  const { me, store, can } = useSession()
  const qc = useQueryClient()
  const extra = useRegistry()
  const [limit, setLimit] = useState<string | null>(null)
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    const unsub = qc.getMutationCache().subscribe(ev => {
      const err = ev.mutation?.state.error
      if (ev.type === 'updated' && err instanceof ApiError && err.code === 'limit_reached') setLimit(err.message)
    })
    const onEvt = (e: Event) => setLimit(String((e as CustomEvent).detail ?? 'You’ve reached a limit on your plan.'))
    window.addEventListener('plan:limit', onEvt)
    return () => { unsub(); window.removeEventListener('plan:limit', onEvt) }
  }, [qc])

  const exitImpersonation = async () => {
    setExiting(true)
    try { await api.post('/api/auth/impersonation/exit', {}) }
    catch (e) { if (!(e instanceof ApiError && (e.status === 401 || e.status === 404))) { toast.error(e); setExiting(false); return } }
    qc.clear()
    window.location.assign('/login')
  }

  const showStore = (kind === 'store' || kind === 'full') && store
  return (
    <div className="flex flex-col">
      {me?.user.impersonating && (
        <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-[repeating-linear-gradient(135deg,var(--accent-soft),var(--accent-soft)_10px,color-mix(in_oklab,var(--accent-soft),var(--accent)_8%)_10px,color-mix(in_oklab,var(--accent-soft),var(--accent)_8%)_20px)] px-4 py-2 text-base text-accent-strong sm:px-6">
          <Eye className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">You are viewing as <span className="font-semibold">{me.user.name || me.user.email}</span>{store ? <> in <span className="font-semibold">{store.name}</span></> : null}. Actions are recorded.</span>
          <Button size="sm" variant="secondary" icon={<LogOut />} loading={exiting} onClick={() => void exitImpersonation()}>Exit</Button>
        </div>
      )}
      {showStore && store.status === 'suspended' && (
        <Banner strip tone="critical" title="This store is suspended">
          {store.statusReason || 'Your storefront is offline and checkout is disabled.'} Reply to our email or contact support to restore it.
        </Banner>
      )}
      {showStore && store.status === 'under_review' && (
        <Banner strip tone="warning" title="Your store is under review">
          {store.statusReason || 'Checkout is paused while we complete a routine safety review — usually within one business day.'} You can keep editing your store in the meantime.
        </Banner>
      )}
      {showStore && store.status === 'closed' && (
        <Banner strip tone="neutral" title="This store is closed">{store.statusReason || 'The storefront is offline.'}</Banner>
      )}
      {limit && kind !== 'platform' && (
        <Banner strip tone="warning" title={`You’ve reached a limit on the ${store?.plan.name ?? 'current'} plan`} onDismiss={() => setLimit(null)}
          actions={can('billing') ? <Button size="sm" variant="primary" asChild><Link to="/settings/plan" onClick={() => setLimit(null)}>See plans</Link></Button> : undefined}>
          {limit}
        </Banner>
      )}
      {extra.map(({ id, def }) => <Banner key={id} strip {...def} />)}
    </div>
  )
}
